// The Assess tab: Claude's latest assessment of a stock (star ratings, soft scores with evidence,
// Gold's hard metrics, scenarios, tracked catalysts and promises) and how it has changed over time.
import { api } from "./api";
import { sparkline } from "./charts";
import { askClaudeUrl } from "./connector";
import { compact, date, dateTime, esc, fixed, pct, price } from "./format";
import { markdown } from "./markdown";

interface Metric { value: number | null; unit: string; score: number | null; note?: string }
interface Soft { score: number; rationale: string; confidence: string; evidence: { url: string; title?: string; date?: string }[]; buzz?: string; posts_30d?: number }
interface Assessment {
  id: number; assessedAt: string; method: string; model: string | null; price: number | null; priceDate: string | null;
  stars: { health: number; risk: number; reward: number }; confidence: string; summary: string;
  soft: Record<string, Soft>; hard: Record<string, Metric>;
  scenarios: Record<"bear" | "base" | "bull", { price: number; probability: number; note?: string } | undefined> | null;
  redFlags: { text: string; date?: string; url?: string }[]; sources: { source: string; reached: boolean; note?: string }[]; changes: string[];
}
interface Item { id: number; kind: string; description: string; expectedDate: string | null; status: string; note: string | null; overdue: boolean }

const SOFT_LABELS: Record<string, string> = {
  sentiment_retail: "Retail sentiment", sentiment_media: "Media and news tone", management_alignment: "Management alignment",
  governance: "Governance", funding_risk: "Funding risk", business_momentum: "Business momentum",
  catalysts: "Catalysts", track_record: "Delivery track record",
};
const SCORE_WORDS: Record<string, string> = { "-2": "Very negative", "-1": "Negative", "0": "Neutral", "1": "Positive", "2": "Very positive" };
const HARD: [string, string, (m: Metric) => string][] = [
  ["runway", "Cash runway", (m) => (m.value != null ? `${fixed(m.value, 1)} months` : m.note ?? "–")],
  ["dilution", "Shares on issue, 1-year change", (m) => pct(m.value, true)],
  ["current_ratio", "Current ratio", (m) => (m.value != null ? `${fixed(m.value, 2)}×` : "–")],
  ["debt_to_equity", "Debt / equity", (m) => (m.value != null ? `${fixed(m.value, 2)}×` : m.note ?? "–")],
  ["fcf_margin", "Free cash flow margin", (m) => pct(m.value, true)],
  ["avg_daily_value", "Average $ traded a day", (m) => (m.value != null ? "$" + compact(m.value) : "–")],
  ["days_to_exit", "Days of volume to sell your holding", (m) => (m.value != null ? fixed(m.value, 1) : m.note ?? "–")],
  ["volatility", "Volatility (1 year)", (m) => pct(m.value)],
  ["concentration", "Share of your portfolio", (m) => pct(m.value)],
  ["ev_to_sales", "EV / sales", (m) => (m.value != null ? `${fixed(m.value, 2)}×` : "–")],
  ["pe", "P/E", (m) => (m.value != null ? fixed(m.value, 1) : m.note ?? "–")],
  ["fcf_yield", "Free cash flow yield", (m) => pct(m.value)],
  ["analyst_upside", "Analyst target upside", (m) => pct(m.value, true)],
  ["price_trend", "Price vs 200-day average", (m) => pct(m.value, true)],
  ["expected_return", "Expected return (scenarios)", (m) => pct(m.value, true) + (m.note ? ` · ${m.note}` : "")],
  ["sentiment_direction", "Sentiment change since last", (m) => (m.value != null ? (m.value > 0 ? "+" : "") + fixed(m.value, 1) : m.note ?? "–")],
];

export function starsHtml(n: number, label?: string) {
  const icons = [1, 2, 3, 4, 5].map((i) => `<i class="${n >= i ? "full" : n >= i - 0.5 ? "half" : "empty"}">★</i>`).join("");
  return `<span class="stars" role="img" aria-label="${n} out of 5 stars${label ? `, ${esc(label)}` : ""}">${icons}</span>`;
}

export const riskWord = (r: number) => (r >= 4.5 ? "Low risk" : r >= 3.5 ? "Moderate risk" : r >= 2.5 ? "Elevated risk" : r >= 1.5 ? "High risk" : "Very high risk");
const chip = (s: number | null | undefined) => (s == null ? `<span class="chip">–</span>` : `<span class="chip s${s}">${SCORE_WORDS[String(s)]}</span>`);
// Compact score marker for long lists: ▲▲ very positive … ▼▼ very negative.
const MARKS: Record<string, string> = { "-2": "▼▼", "-1": "▼", "0": "●", "1": "▲", "2": "▲▲" };
const mark = (s: number | null | undefined) => (s == null ? `<span class="mark">–</span>` : `<span class="mark s${s}" title="${SCORE_WORDS[String(s)]}">${MARKS[String(s)]}</span>`);

const askButton = (code: string, again = false) =>
  `<a class="ask" href="${esc(askClaudeUrl(code))}" target="_blank" rel="noopener">Ask Claude to ${again ? "Re-assess" : "Assess"} ${esc(code)}</a>`;

export async function assessPanel(code: string): Promise<string> {
  let data: { assessments: Assessment[]; items: Item[] };
  try {
    data = await api(`stock/${code}/assessments`);
  } catch (e) {
    return `<div class="notice error">${esc((e as Error).message)}</div>`;
  }
  const [a, ...older] = data.assessments;
  if (!a) {
    return `<section class="card"><h3>Not assessed yet</h3>
      <p>Claude can build a file on ${esc(code)} in your Claude app (with the Gold connector and web search on): it reads what Gold already holds, researches news, announcements and forum sentiment, scores it with Gold's guide and saves the result here, dated, so changes can be followed over time.</p>
      ${askButton(code)}</section>`;
  }
  const out: string[] = [];

  out.push(`<section class="card scorecard">
    <div class="ratings">
      <div><dt>Investment Health</dt><dd>${starsHtml(a.stars.health)}<b>${a.stars.health}</b></dd></div>
      <div><dt>Risk <small class="muted">(more ★ = safer)</small></dt><dd>${starsHtml(a.stars.risk, riskWord(a.stars.risk))}<b>${riskWord(a.stars.risk)}</b></dd></div>
      <div><dt>Reward</dt><dd>${starsHtml(a.stars.reward)}<b>${a.stars.reward}</b></dd></div>
    </div>
    <p class="muted small">Assessed ${dateTime(a.assessedAt)}${a.model ? ` by ${esc(a.model)}` : ""} · confidence ${esc(a.confidence)} · price then ${price(a.price)} · method ${esc(a.method)}</p>
    ${askButton(code, true)}
  </section>`);

  out.push(`<section class="card"><h3>What changed</h3><ul class="plain changes">${a.changes.map((c) => `<li>${esc(c)}</li>`).join("")}</ul></section>`);
  out.push(`<section class="card"><h3>Summary</h3><div class="md">${markdown(a.summary)}</div></section>`);

  out.push(`<section class="card"><h3>Claude's scores</h3><ul class="soft">${Object.keys(SOFT_LABELS).filter((k) => a.soft[k]).map((k) => {
    const s = a.soft[k];
    const prev = older[0]?.soft?.[k]?.score;
    const moved = prev != null && prev !== s.score ? ` <small class="${s.score > prev ? "up" : "down"}">${s.score > prev ? "▲" : "▼"} from ${SCORE_WORDS[String(prev)].toLowerCase()}</small>` : "";
    const buzz = k === "sentiment_retail" && (s.buzz || s.posts_30d != null) ? `<span class="muted small">Buzz ${esc(s.buzz ?? "–")}${s.posts_30d != null ? ` · ${s.posts_30d} posts in 30 days` : ""}</span>` : "";
    return `<li><div class="soft-head"><b>${SOFT_LABELS[k]}</b>${chip(s.score)}${moved}</div>
      <p>${esc(s.rationale)}</p>${buzz}
      <span class="muted small">Confidence ${esc(s.confidence)}${s.evidence.length ? " · " + s.evidence.map((e, i) => `<a href="${esc(e.url)}" target="_blank" rel="noopener">${esc(e.title || `source ${i + 1}`)}</a>${e.date ? ` (${date(e.date)})` : ""}`).join(", ") : ""}</span></li>`;
  }).join("")}</ul></section>`);

  out.push(`<section class="card"><h3>Gold's numbers at the time</h3><ul class="hard">${HARD.filter(([k]) => a.hard[k]).map(([k, label, fmt]) =>
    `<li><span>${label}</span><b>${esc(fmt(a.hard[k]))}</b>${mark(a.hard[k].score)}</li>`).join("")}</ul>
    <p class="muted small">▲▲ very positive · ▲ positive · ● neutral · ▼ negative · ▼▼ very negative, for the rating.</p></section>`);

  if (a.scenarios) {
    const rows = (["bear", "base", "bull"] as const).filter((k) => a.scenarios![k]).map((k) => {
      const s = a.scenarios![k]!;
      return `<tr><th>${k[0].toUpperCase() + k.slice(1)}</th><td>${price(s.price)}</td><td>${pct(s.probability)}</td><td>${a.price ? pct(s.price / a.price - 1, true) : "–"}</td><td class="muted">${esc(s.note ?? "")}</td></tr>`;
    }).join("");
    out.push(`<section class="card"><h3>12-month scenarios</h3><div class="table-wrap"><table><thead><tr><th></th><th>Price</th><th>Chance</th><th>vs then</th><th></th></tr></thead><tbody>${rows}</tbody></table></div>
      <p class="muted small">Expected price ${price(a.hard.scenarios?.value)} (${pct(a.hard.expected_return?.value, true)})${a.hard.expected_return?.note ? `, ${esc(a.hard.expected_return.note)}` : ""}.</p></section>`);
  }

  if (data.items.length) {
    out.push(`<section class="card"><h3>Catalysts and promises</h3><ul class="items">${data.items.map((i) => `<li class="${i.overdue ? "overdue" : ""}">
      <span class="status ${esc(i.status)}">${i.overdue ? "Overdue" : esc(i.status[0].toUpperCase() + i.status.slice(1))}</span>
      <div><b>${esc(i.description)}</b><span class="muted small">${i.kind === "promise" ? "Promise" : "Catalyst"}${i.expectedDate ? ` · due ${date(i.expectedDate)}` : ""}${i.note ? ` · ${esc(i.note)}` : ""}</span></div></li>`).join("")}</ul></section>`);
  }

  if (a.redFlags.length) {
    out.push(`<section class="card"><h3>Red flags</h3><ul class="plain flags">${a.redFlags.map((f) => `<li>⚑ ${f.url ? `<a href="${esc(f.url)}" target="_blank" rel="noopener">${esc(f.text)}</a>` : esc(f.text)}${f.date ? ` <span class="muted small">${date(f.date)}</span>` : ""}</li>`).join("")}</ul></section>`);
  }
  if (a.sources.length) {
    out.push(`<section class="card"><h3>Sources checked</h3><ul class="plain">${a.sources.map((s) => `<li>${s.reached ? "✓" : "✗"} ${esc(s.source)}${s.note ? ` <span class="muted small">${esc(s.note)}</span>` : ""}</li>`).join("")}</ul></section>`);
  }

  const all = [a, ...older];
  if (all.length > 1) {
    const chron = all.slice().reverse();
    out.push(`<section class="card"><h3>History</h3>
      <div class="spark-wrap">${sparkline(chron.map((x) => x.stars.health), 320, 56)}</div>
      <div class="table-wrap"><table><thead><tr><th>Date</th><th>Health</th><th>Risk</th><th>Reward</th><th>Price</th></tr></thead><tbody>
      ${all.map((x) => `<tr><td>${date(x.assessedAt)}</td><td>${x.stars.health}★</td><td>${x.stars.risk}★</td><td>${x.stars.reward}★</td><td>${price(x.price)}</td></tr>`).join("")}</tbody></table></div></section>`);
  }
  return `<div class="assess">${out.join("")}</div>`;
}
